import { HashLocationStrategy, LocationStrategy, registerLocaleData } from "@angular/common";
import { provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import localeFr from "@angular/common/locales/fr";
import type { ApplicationConfig } from "@angular/core";
import { ErrorHandler, importProvidersFrom, inject, isDevMode, LOCALE_ID, provideAppInitializer } from "@angular/core";
import { initializeApp, provideFirebaseApp } from "@angular/fire/app";
import { Auth, getAuth, provideAuth, user } from "@angular/fire/auth";
import { MatButtonModule } from "@angular/material/button";
import { MatNativeDateModule } from "@angular/material/core";
import { MatIconModule } from "@angular/material/icon";
import { MatRadioModule } from "@angular/material/radio";
import { BrowserModule } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter, Router, withComponentInputBinding } from "@angular/router";
import { AkitaNgRouterStoreModule } from "@datorama/akita-ng-router-store";
import { AkitaNgDevtools } from "@datorama/akita-ngdevtools";
import { provideTransloco, TranslocoService } from "@jsverse/transloco";
import { provideFormFieldError } from "@recursyve/ngx-material-components/form-field-error";
import { provideNiceFilterViewTranslater } from "@recursyve/nice-filter-view/common";
import { createErrorHandler, TraceService } from "@sentry/angular";
import { FALLBACK, GravatarModule } from "ngx-gravatar";
import { provideEnvironmentNgxMask } from "ngx-mask";
import { provideToastr } from "ngx-toastr";
import { take } from "rxjs/operators";
import { Manager } from "socket.io-client";

import { FR_MAT_PAGINATOR_PROVIDER } from "../../public/assets/i18n/paginator.i18n";
import { LanguagePreloadService } from "../common/i18n/language-preloader.service";
import { TranslocoHttpLoader } from "../common/i18n/transloco/transloco-loader";
import { environment } from "../environments/environment";
import { ApiModule } from "./api/api.module";
import { appRoutes } from "./app.routes";
import { GlobalErrorHandler } from "./handlers/global-error.handler";

registerLocaleData(localeFr);

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom([
            BrowserModule,
            MatNativeDateModule,
            MatButtonModule,
            MatIconModule,
            ApiModule,
            environment.production ? [] : AkitaNgDevtools,
            AkitaNgRouterStoreModule,
            MatRadioModule,
            GravatarModule.forRoot({ fallback: FALLBACK.mm })
        ]),
        FR_MAT_PAGINATOR_PROVIDER,
        provideAppInitializer(() => inject(LanguagePreloadService).preload()),
        provideAppInitializer(() => user(inject(Auth)).pipe(take(1))),
        provideAppInitializer(() => {
            inject(TraceService);
        }),
        { provide: LocationStrategy, useClass: HashLocationStrategy },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        { provide: LOCALE_ID, useValue: "fr" },
        {
            provide: Manager,
            useValue: new Manager(environment.wsUrl, {
                autoConnect: false,
                withCredentials: true,
                transports: ["websocket", "polling"]
            })
        },
        provideFirebaseApp(() => initializeApp(environment.fire)),
        provideHttpClient(withInterceptorsFromDi()),
        provideEnvironmentNgxMask(),
        provideAuth(() => getAuth()),
        provideToastr({
            positionClass: "toast-bottom-right",
            tapToDismiss: true,
            toastClass: "toast"
        }),
        provideAnimations(),
        provideFormFieldError({
            translater: {
                useFactory: (service: TranslocoService) => (key, params) => service.translate(key, params),
                deps: [TranslocoService]
            }
        }),
        {
            provide: ErrorHandler,
            useValue: createErrorHandler()
        },
        {
            provide: TraceService,
            deps: [Router]
        },
        provideTransloco({
            config: {
                availableLangs: environment.language.availableLangs,
                defaultLang: environment.language.defaultLang,
                reRenderOnLangChange: false,
                prodMode: !isDevMode()
            },
            loader: TranslocoHttpLoader
        }),
        provideNiceFilterViewTranslater({
            translater: {
                useFactory: (service: TranslocoService) => (key, params) => service.translate(key, params),
                deps: [TranslocoService]
            }
        }),
        provideRouter(appRoutes, withComponentInputBinding())
    ]
};
